/**
 * Landing page for one flywheel section, Admin, or Help.
 * Lists only the tools the viewer is allowed to open.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { usePermissions } from '../hooks/usePermissions';
import {
  HELP_LINKS,
  getArea,
  toolLinkLabel,
  visibleTools,
} from '../config/permissions';
import { get } from '../api/client';
import { useSurveyAssignment } from '../hooks/useSurveyAssignment';
import './SectionPage.css';

function useHasAssignedCheckup(enabled) {
  const [hasAssignedCheckup, setHasAssignedCheckup] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancel = false;
    get('/api/surveys/my')
      .then(({ ok, data }) => {
        if (!cancel && ok && Array.isArray(data) && data.length > 0) {
          setHasAssignedCheckup(true);
        }
      })
      .catch(() => {});
    return () => {
      cancel = true;
    };
  }, [enabled]);

  return hasAssignedCheckup;
}

const SectionPage = ({ sectionId }) => {
  const area = getArea(sectionId);
  const { hasPermission, isAdmin } = usePermissions();
  const needsCheckupLookup = sectionId === 'leaders' && !isAdmin;
  const hasAssignedCheckup = useHasAssignedCheckup(needsCheckupLookup);
  const hasLeadersSurvey = useSurveyAssignment();

  if (!area) {
    return (
      <div className="section-page">
        <PageHeader title="Section" backTo="/" backLabel="← Home" />
        <p>This section is not available.</p>
      </div>
    );
  }

  const access = { hasPermission, isAdmin, hasAssignedCheckup, hasLeadersSurvey };
  const tools = sectionId === 'help'
    ? HELP_LINKS
    : visibleTools(sectionId, access).map((route) => ({
      path: route.path,
      label: toolLinkLabel(route),
      blurb: route.blurb,
    }));

  return (
    <div className="section-page">
      <PageHeader title={area.title} backTo="/" backLabel="← Home">
        <p className="section-lead">
          {area.letter && <span className="letter-badge section-letter">{area.letter}</span>}
          <span>{area.description}</span>
        </p>
      </PageHeader>

      {tools.length > 0 ? (
        <nav aria-label={`${area.title} tools`}>
          <ul className="section-tools">
            {tools.map((tool) => (
              <li key={tool.path}>
                <Link to={tool.path} className="section-tool">
                  <span className="section-tool-title">{tool.label}</span>
                  {tool.blurb && <span className="section-tool-blurb">{tool.blurb}</span>}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      ) : (
        <p className="section-empty">
          None of the current tools in this section are open for your account. An administrator can grant access.
        </p>
      )}

      {area.comingSoon && <p className="section-soon">{area.comingSoon}</p>}
    </div>
  );
};

export default SectionPage;
